import {useMemo} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useNavigate} from "react-router-dom";
import {message} from "antd";
import {AxiosError} from "axios";
import {EditDiscountCodeFormValuesV2, GetDiscountCodeApiResponse} from "../types";
import {buildV2UpdatePayload} from "../service-v2";
import {useGetDiscountCodeContext} from "../context";
import {AnalyticsEvent} from "../../../../utils/analytics";
import {useClient} from "../../../../utils/hooks";
import {UNLIMITED_CUSTOMERS} from "../../create-discount-code/create-discount-code-service";
import {useGetOfferings} from "../../../../react-query-hooks/offerings";

export const useUpdateDiscountCodeMutationV2 = () => {
  const queryClient = useQueryClient();
  const client = useClient("DISCOUNTS");
  const offeringsQuery = useGetOfferings();
  const allOfferings = offeringsQuery.data ?? [];

  return useMutation({
    mutationFn: ({
      id,
      values,
    }: {
      id: number;
      values: EditDiscountCodeFormValuesV2 & {id: number};
    }) => {
      const payload = buildV2UpdatePayload(values, allOfferings);
      return client(`discounts/${id}`, {
        method: "PUT",
        data: payload,
      });
    },
    onMutate: (variables) => {
      queryClient.cancelQueries(["get_discount_code", String(variables.id)]);

      const oldDiscountCode = queryClient.getQueryData<GetDiscountCodeApiResponse>([
        "get_discount_code",
        String(variables.id),
      ]);

      return () =>
        queryClient.setQueryData(
          ["get_discount_code", String(variables.id)],
          oldDiscountCode
        );
    },
    onError: (error: AxiosError<{message: string}>, variables, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Discounts", "Edit discount fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: () => {
      AnalyticsEvent("Discounts", "Edit discount success");
    },
    onSettled: (d, e, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["get_discount_code", String(variables.id)],
        }),
        queryClient.invalidateQueries({
          queryKey: ["discounts"],
        }),
      ]),
  });
};

interface HookResult {
  updateDiscountCode(values: EditDiscountCodeFormValuesV2): void;
  defaultFormValues: EditDiscountCodeFormValuesV2;
  wasDiscountCodeUpdated: boolean;
  isUpdatingDiscountCode: boolean;
  responseData?: unknown;
}

export function useUpdateDiscountCodeV2(): HookResult {
  const navigate = useNavigate();
  const offeringsQuery = useGetOfferings();
  const allOfferings = offeringsQuery.data ?? [];
  const {
    state: {
      discountCodeData: {
        discount,
        bookingOfferings,
        liveClassOfferings,
        productOfferings,
        productServiceOfferings,
        subscriptionOfferings,
        selectedOfferings: fetchedSelectedOfferings,
        isAllOfferings,
      },
    },
  } = useGetDiscountCodeContext();

  const mutation = useUpdateDiscountCodeMutationV2();

  const updateDiscountCode = (values: EditDiscountCodeFormValuesV2) => {
    mutation.mutate(
      {id: discount.id, values: {...values, id: discount.id}},
      {
        onSuccess() {
          message.success("Your discount code has been updated successfully!");
          navigate("/discounts");
        },
      }
    );
  };

  const selectedOfferings = useMemo(() => {
    const selectedValues = new Set<string>();

    fetchedSelectedOfferings?.forEach((selectedOffering) => {
      const matchedOffering = allOfferings.find(
        (offering) =>
          offering.offeringId === selectedOffering.offeringId &&
          offering.offeringName === selectedOffering.offeringName
      );

      if (matchedOffering) {
        selectedValues.add(matchedOffering.value);
      }
    });

    if (selectedValues.size === 0) {
      const offeringsMap = new Map(allOfferings.map((o) => [o.offeringId, o]));

      bookingOfferings.forEach((offering) => {
        const opt = offeringsMap.get(offering.id);
        if (opt) selectedValues.add(opt.value);
      });
      liveClassOfferings.forEach((offering) => {
        const opt = offeringsMap.get(offering.id);
        if (opt) selectedValues.add(opt.value);
      });
      productOfferings.forEach((offering) => {
        const opt = offeringsMap.get(offering.id);
        if (opt) selectedValues.add(opt.value);
      });
      (productServiceOfferings ?? []).forEach((offering) => {
        const opt = offeringsMap.get(offering.id);
        if (opt) selectedValues.add(opt.value);
      });
      (subscriptionOfferings ?? []).forEach((offering) => {
        const opt = offeringsMap.get(offering.id);
        if (opt) selectedValues.add(opt.value);
      });
    }

    return Array.from(selectedValues);
  }, [
    allOfferings,
    bookingOfferings,
    fetchedSelectedOfferings,
    liveClassOfferings,
    productOfferings,
    productServiceOfferings,
    subscriptionOfferings,
  ]);

  const defaultFormValues = useMemo<EditDiscountCodeFormValuesV2>(
    () => ({
      customersType:
        discount.usage_limit === UNLIMITED_CUSTOMERS ? "unlimited" : "limited",
      name: discount.code,
      percentage_off: discount.percentage_off,
      usage_limit: discount.usage_limit,
      offerings: isAllOfferings
        ? allOfferings.map((offering) => offering.value)
        : selectedOfferings,
      usage: discount.is_multiple_use ? "multiple" : "one time",
      validity: [discount.start_date, discount.end_date],
      disable_discount: discount.status_id === 1 ? "enabled" : "disabled",
      selectAllOfferings: isAllOfferings ?? false,
    }),
    [allOfferings, discount, isAllOfferings, selectedOfferings]
  );

  return {
    updateDiscountCode,
    defaultFormValues,
    wasDiscountCodeUpdated: mutation.isSuccess,
    isUpdatingDiscountCode: mutation.isLoading,
    responseData: mutation.data?.data,
  };
}
